import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { SparklesIcon } from '@components/common/ui/icons';
import { inlineAction, InlineAction } from '@service/AiService';
import * as styles from './InlineAiMenu.module.css';

const ACTIONS: { labelKey: string; action: InlineAction }[] = [
    { labelKey: 'inlineAi.actions.fix', action: 'fix' },
    { labelKey: 'inlineAi.actions.shorten', action: 'shorten' },
    { labelKey: 'inlineAi.actions.simplify', action: 'simplify' },
    { labelKey: 'inlineAi.actions.structure', action: 'structure' },
    { labelKey: 'inlineAi.actions.toRu', action: 'translate_to_ru' },
    { labelKey: 'inlineAi.actions.toEn', action: 'translate_to_en' },
    { labelKey: 'inlineAi.actions.continueText', action: 'continue' },
];

interface InlineAiMenuProps {
    selection: { text: string; from: number; to: number; rect: DOMRect; doReplace: (md: string) => void; doInsertAfter: (md: string) => void; } | null;
    markdown: string;
    onReplace: (result: string) => void;
    onInsertAfter: (result: string) => void;
    onDismiss: () => void;
}

type Phase = 'idle' | 'loading' | 'result';

export const InlineAiMenu: React.FC<InlineAiMenuProps> = ({
    selection,
    markdown,
    onReplace,
    onInsertAfter,
    onDismiss,
}) => {
    const { t } = useTranslation();
    const [phase, setPhase] = useState<Phase>('idle');
    const [result, setResult] = useState('');
    const wrapperRef = useRef<HTMLDivElement>(null);

    // Reset to idle when selection changes
    useEffect(() => {
        setPhase('idle');
        setResult('');
    }, [selection?.text, selection?.from]);

    // Close on outside click
    useEffect(() => {
        if (!selection) return;
        const handler = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                onDismiss();
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [selection, onDismiss]);

    if (!selection) return null;

    const { rect } = selection;
    const top = rect.top - 44; // above selection
    const left = rect.left;

    const handleAction = async (action: InlineAction) => {
        setPhase('loading');
        try {
            const res = await inlineAction(action, selection.text, markdown);
            setResult(res);
            setPhase('result');
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : t('inlineAi.requestFailed');
            setResult('⚠️ ' + message);
            setPhase('result');
        }
    };

    const handleReplace = () => {
        onReplace(result);
        onDismiss();
    };

    const handleInsertAfter = () => {
        onInsertAfter(result);
        onDismiss();
    };

    const menu = (
        <div
            ref={wrapperRef}
            className={styles.wrapper}
            style={{ top: Math.max(8, top), left: Math.max(8, left) }}
        >
            {phase === 'idle' && (
                <div className={styles.actionBar}>
                    <SparklesIcon className={styles.aiIcon} />
                    <span className={styles.sep} />
                    {ACTIONS.map((a, i) => (
                        <React.Fragment key={a.action}>
                            {i > 0 && <span className={styles.sep} />}
                            <button
                                className={styles.actionBtn}
                                onClick={() => handleAction(a.action)}
                            >
                                {t(a.labelKey)}
                            </button>
                        </React.Fragment>
                    ))}
                </div>
            )}
            {phase === 'loading' && (
                <div className={styles.loading}>
                    <span className={styles.spinner} />
                    {t('inlineAi.generating')}
                </div>
            )}
            {phase === 'result' && (
                <div className={styles.resultPopup}>
                    <div className={styles.resultText}>{result}</div>
                    <div className={styles.resultActions}>
                        <button className={`${styles.resultBtn} ${styles.resultBtnPrimary}`} onClick={handleReplace}>
                            {t('inlineAi.replace')}
                        </button>
                        <button className={styles.resultBtn} onClick={handleInsertAfter}>
                            {t('inlineAi.insertAfter')}
                        </button>
                        <button className={styles.resultBtn} onClick={() => setPhase('idle')}>
                            {t('inlineAi.back')}
                        </button>
                        <button className={styles.resultBtn} onClick={onDismiss} title={t('inlineAi.close')}>
                            {t('inlineAi.close')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );

    return createPortal(menu, document.body);
};
